// File created by Reginald Sahil Chand on 26-03-2024.

import React, {type MouseEventHandler, type ReactElement} from 'react';

import {motion} from 'framer-motion';
import {useMediaQuery} from '@react-hook/media-query';
import {MdOutlineClose} from 'react-icons/md';

import '../../../../custom.css';
import {Logo} from '../logo/Logo';
import {Button} from '../../button/Button';

import {type TaskNavigationDataProps} from '../../../../types/data/taskNavigationDataTypes';

/**
 * @author Reginald Sahil Chand
 * @email Developer.ReginaldSahilChand@gmail.com
 *
 * @see taskNavigationData.tsx
 * @type {TaskNavigationDataProps[]} - taskNavigationData - Custom type used in taskNavigationData.
 * @version 0.1.0
 *
 * @property {TaskNavigationDataProps[]} - taskNavigationData - Custom type used in taskNavigationData.
 * @property {boolean} taskNavigationDrawerState - Provides a state of true or false based on an onclick event.
 * @property {void} taskNavigationDrawerFunction - Function to toggle drawer between opening and closing based on,
 *                  taskNavigationDrawerState.
 * @property {number} activeIndexState - Retrieves the index of the current clicked element (task link) based on its text-content.
 * @property {MouseEventHandler<HTMLDivElement>} taskNavigationLinkFunction - This function is responsible to change the,
 * 												 activeIndexState.
 */
type TaskNavigationProps = {
	taskNavigationData: TaskNavigationDataProps[];
	taskNavigationDrawerState: boolean;
	taskNavigationDrawerFunction: () => void;

	activeIndexState: number;
	taskNavigationLinkFunction: MouseEventHandler<HTMLDivElement>;
};

/**
 * @author Reginald Sahil Chand
 * @email Developer.ReginaldSahilChand@gmail.com
 *
 * @description Renders list of given task navigation links as props from an array.
 *
 * @type {TaskNavigationProps}
 * @version 0.1.0
 *
 * @returns {JSX Element} TaskNavigation component.
 */
export const TaskNavigation: React.FC<TaskNavigationProps> = React.memo(
	({
		taskNavigationData,
		taskNavigationDrawerState,
		taskNavigationDrawerFunction,
		activeIndexState,
		taskNavigationLinkFunction,
	}: TaskNavigationProps): ReactElement => {
		const isDesktop: boolean = useMediaQuery('(min-width: 768px)');

		/**
		 * @author Reginald Sahil Chand
		 * @email Developer.ReginaldSahilChand@gmail.com
		 *
		 * @description Invokes the below functions.
		 * @version 0.1.0
		 *
		 * @param event Mouse Event. (Used to get the text-content of the clicked element).
		 */
		const taskNavigationDrawerAndNavigationLinkFunction: MouseEventHandler<HTMLDivElement> = (
			event,
		) => {
			taskNavigationDrawerFunction();
			taskNavigationLinkFunction(event);
		};

		return (
			<motion.nav
				animate={
					isDesktop
						? ''
						: taskNavigationDrawerState && taskNavigationDrawerState
							? {translateX: 0}
							: {translateX: -1000}
				}
				className="w-full h-screen md:h-[unset] fixed top-0 left-0 z-10 bg-taskNavigation overflow-y-auto md:static"
			>
				<div className="h-[6rem] mb-5 p-5 sticky top-0 left-0 z-50 flex items-center justify-between bg-header md:hidden">
					<Logo logoText="Task Quazar" />
					<Button
						buttonIcon={<MdOutlineClose />}
						buttonDescription={
							taskNavigationDrawerState && taskNavigationDrawerState
								? 'Close Task Navigation Drawer'
								: 'Open Task Navigation Drawer'
						}
						buttonFunction={taskNavigationDrawerFunction}
					/>
				</div>

				<ul className="w-full p-5 pt-0 md:pt-5 overflow-y-auto">
					{taskNavigationData?.map((data, index) => (
						<li tabIndex={0} key={index} className="w-full h-20 mb-5 last-child-mb-0">
							<div
								onClick={taskNavigationDrawerAndNavigationLinkFunction}
								className={`h-full p-5 flex items-center justify-start md:cursor-pointer rounded-lg ${index === activeIndexState ? 'bg-primary' : ''}  transition-colors duration-300 ease-in-out hover:bg-primary`}
							>
								<span className="w-[3.125rem] h-[3.125rem] mr-5 flex items-center justify-center scale-125 rounded-lg text-text bg-primary">
									{data?.icon}
								</span>

								<p
									title={data?.description}
									aria-label={data?.description}
									className="text-base md:text-lg font-medium text-text"
								>
									{data?.title}
								</p>
							</div>
						</li>
					))}
				</ul>
			</motion.nav>
		);
	},
);

TaskNavigation.displayName = 'TaskNavigation';
